import type {
  CSSObject,
  DefaultMantineColor,
  MantineThemeOverride,
} from "@mantine/core";
import type { Tuple } from "@mantine/core";

export type CustomColor = "background" | "primary" | "secondary" | "accent";

declare module "@mantine/core" {
  export interface MantineThemeColorsOverride {
    colors: Record<DefaultMantineColor | CustomColor, Tuple<string, 10>>;
  }
}

const BACKGROUND_COLOR = "#FAFBFB";

export const APP_THEME: MantineThemeOverride = {
  colorScheme: "light",
  colors: {
    background: new Array(10).fill(BACKGROUND_COLOR) as Tuple<CustomColor, 10>,
    primary: [
      "#dae3ee",
      "#c1d0e2",
      "#a8bdd6",
      "#90aacb",
      "#7797bf",
      "#5e84b3",
      "#4c72a1",
      "#406088",
      "#344f6f",
      "#293d57",
    ],
    secondary: [
      "#d9eeeb",
      "#c0e3de",
      "#a7d7d0",
      "#8eccc3",
      "#75c1b6",
      "#5cb5a8",
      "#4aa396",
      "#3e8a7f",
      "#337168",
      "#285851",
    ],
    accent: [
      "#ffe0d9",
      "#ffccbf",
      "#ffb8a6",
      "#ffa38c",
      "#ff8f73",
      "#ff7a59",
      "#ff6640",
      "#ff5226",
      "#ff3d0d",
      "#f23000",
    ],
  },
  primaryColor: "primary",
  fontFamily:
    "Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, " +
    "Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji",
  fontFamilyMonospace:
    "JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, " +
    "Liberation Mono, Courier New, monospace",
  headings: {
    fontFamily:
      "Epilogue, Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, " +
      "Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji",
  },
  focusRingStyles: {
    styles: ({ colors }) => ({
      outlineOffset: rem(2),
      outlineStyle: "solid",
      outlineWidth: rem(1.5),
      outlineColor: colors.accent[4],
    }),
  },
  globalStyles: ({ colors, fn }) => ({
    body: {
      backgroundColor: colors.background[fn.primaryShade()],
    },
  }),
  components: {
    Alert: {
      defaultProps: {
        radius: "md",
      },
    },
    Button: {
      defaultProps: {
        radius: "md",
        color: "primary",
      },
    },
    Card: {
      defaultProps: {
        radius: "md",
      },
    },
    Loader: {
      defaultProps: {
        color: "accent",
      },
    },
    LoadingOverlay: {
      defaultProps: {
        loaderProps: {
          size: "sm",
        },
      },
    },
    ThemeIcon: {
      defaultProps: {
        variant: "default",
      },
    },
    Input: {
      defaultProps: {
        radius: "md",
      },
      styles: ({ fontSizes, fn }, params, { size }) => ({
        input: {
          ...(size === "sm" && {
            [fn.smallerThan("xs")]: {
              fontSize: fontSizes.md,
            },
          }),
        },
      }),
    },
    PasswordInput: {
      styles: ({ fontSizes, fn }, params, { size }) => ({
        innerInput: {
          ...(size === "sm" && {
            [fn.smallerThan("xs")]: {
              fontSize: fontSizes.md,
            },
          }),
        },
      }),
    },
    MultiSelect: {
      styles: ({ fontSizes, fn }, params, { size }) => ({
        searchInput: {
          ...(size === "sm" && {
            [fn.smallerThan("xs")]: {
              fontSize: fontSizes.md,
            },
          }),
        },
      }),
    },
    Textarea: {
      styles: ({ fontSizes, fn }, params, { size }) => ({
        input: {
          ...(size === "sm" && {
            [fn.smallerThan("xs")]: {
              fontSize: fontSizes.md,
            },
          }),
        },
      }),
    },
    Anchor: {
      defaultProps: {
        color: "secondary",
      },
    },
    Modal: {
      styles: ({ headings: { sizes, ...style } }) => ({
        title: {
          ...(sizes.h3 as CSSObject),
          ...(style as CSSObject),
        },
        content: {
          maxHeight: `calc(100dvh - (5dvh * 2))`,
        },
      }),
    },
  },
};

export const EMAIL_THEME: MantineThemeOverride = {
  ...APP_THEME,
  globalStyles: ({ colors, fn }) => ({
    body: {
      margin: 8,
      fontSize: 14,
      backgroundColor: colors.background[fn.primaryShade()],
    },
  }),
};
